import type { JsonValue } from 'type-fest';

import type { CarouselCard } from '@/shared/types/action';
import type { Datable, Insight } from '@/shared/types/insights';
import type { ToolInvocation } from '@/shared/types/tryIt';

export type StartingContext = Record<string, unknown>;

export type SimulationChannelType = 'chat' | 'email' | 'voice';

export const SIMULATION_CHANNEL_TYPES: SimulationChannelType[] = ['chat', 'email', 'voice'];

export type ScheduleCadence = 'weekdays' | 'weekly' | 'monthly';

export const SCHEDULE_CADENCES: ScheduleCadence[] = ['weekdays', 'weekly', 'monthly'];

export type RunStatus = 'pending' | 'running' | 'completed' | 'failed' | 'canceled';

export type EvaluationStatus = 'pending' | 'running' | 'pass' | 'fail' | 'error';

export type TriggerType = 'manual' | 'scheduled';

export interface Simulation extends Datable {
  readonly simulation_id: string;
  readonly brain_id: string;
  created_by: string | null;
  name: string;
  scenario: string;
  pass_criteria: string;
  channel_type: SimulationChannelType;
  category: string | null;
  max_turns: number;
  schedule_frequency: ScheduleCadence | null;
  starting_context?: StartingContext;
}

export type NewSimulation = Pick<
  Simulation,
  | 'name'
  | 'scenario'
  | 'pass_criteria'
  | 'channel_type'
  | 'category'
  | 'max_turns'
  | 'schedule_frequency'
  | 'starting_context'
>;

export interface SimulationRun extends Datable {
  readonly run_id: string;
  readonly brain_id: string;
  brain_version: number;
  triggered_by: string | null;
  trigger_type: TriggerType;
  schedule_id: string | null;
  status: RunStatus;
  filter_category: string | null;
  total_count: number;
  pass_count: number;
  fail_count: number;
  error_count: number;
  started_at: string | null;
  completed_at: string | null;
  success_rate: number | null;
  duration_seconds: number | null;
  has_fails: boolean;
}

export interface SimulationEvaluation extends Datable {
  readonly evaluation_id: string;
  readonly run_id: string;
  readonly simulation_id: string;
  readonly brain_id: string;
  status: EvaluationStatus;
  evaluator_reasoning: string | null;
  turn_count: number | null;
  duration_seconds: number | null;
  session_id: string | null;
  error_code: string | null;
  error_message: string | null;
}

export type EvaluationWithSimulation = SimulationEvaluation & {
  simulation: Pick<
    Simulation,
    | 'simulation_id'
    | 'name'
    | 'scenario'
    | 'pass_criteria'
    | 'channel_type'
    | 'category'
    | 'max_turns'
    | 'updated'
  >;
};

export interface SimulationsListResponse {
  simulations: Simulation[];
  next_cursor?: string;
}

export interface RunsListResponse {
  runs: SimulationRun[];
  pagination: { has_more: boolean; next_cursor: string | null; total?: number };
}

export interface RunsListParams {
  status?: RunStatus[];
  with_fails?: boolean;
  trigger_type?: TriggerType;
  sort?: string;
  started_after?: string;
  started_before?: string;
}

export interface EvaluationsListResponse {
  evaluations: EvaluationWithSimulation[];
  next_cursor?: string;
}

export interface EvaluationInsightResult {
  insight_id: string;
  name: string;
  type: Insight['type'];
  value: JsonValue;
  reasoning: string | null;
  error: string | null;
}

export interface EvaluationInsightsResponse {
  insights: EvaluationInsightResult[];
}

export type ScheduleFrequency = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

export interface SimulationSchedule extends Datable {
  readonly schedule_id: string;
  readonly brain_id: string;
  created_by: string | null;
  name: string;
  cron_expression: string;
  filter_category: string | null;
  is_active: boolean;
  last_run_id: string | null;
  last_run_at: string | null;
  simulation_ids: string[] | null;
}

export interface SchedulesListResponse {
  simulation_schedules: SimulationSchedule[];
}

// --- get_simulation (analytics Hasura function) ---

export interface SimulationTurnInput {
  text: string;
  type: string;
}

export interface SimulationTurnResponse {
  type: string;
  text?: string;
  action_id?: string;
  // carousel
  cards?: CarouselCard[];
  // webview / survey / media
  url?: string;
  name?: string;
  label?: string;
  // text options (quick replies)
  options?: Array<{ text: string; label: string }>;
}

export interface SimulationTurnIntent {
  intent: string;
  confidence?: number;
}

export interface SimulationTurnEntity {
  entity: string;
  value?: unknown;
}

export interface SimulationTurnDebugStep {
  node_id?: string;
  node_name?: string;
  type?: number;
  sub_type?: number;
  [key: string]: unknown;
}

export interface SimulationTurnDebug {
  steps?: SimulationTurnDebugStep[];
  logs?: string[];
  nodes_stack?: Array<{ node_id: string; name: string; type?: string; request_id?: string }>;
  standalone_question?: string;
  tools_invoked?: ToolInvocation[];
  [key: string]: unknown;
}

export interface SimulationTurnOutput {
  responses: SimulationTurnResponse[];
  debug?: SimulationTurnDebug;
  intents?: SimulationTurnIntent[];
  entities?: SimulationTurnEntity[];
  planner_intents?: string[];
}

export interface SimulationTurnCorrection {
  correction: string;
  edited_by: string;
  language: string;
  time: string;
}

export interface SimulationTurn {
  sent_at: string;
  replied_at: string;
  duration_ms: number;
  request_id?: string;
  input: SimulationTurnInput;
  output: SimulationTurnOutput;
  context: Record<string, unknown> | null;
  corrections?: SimulationTurnCorrection[] | null;
}

export interface SimulationSession {
  account_id: string;
  session_id: string;
  simulation_run_id: string;
  simulation_evaluation_id: string;
  channel_type: string;
  start_time: string;
  end_time: string;
  num_turns: number;
  brain_id: string | null;
  brain_parent_id: string | null;
  brain_version: number | null;
  turns: SimulationTurn[];
}

export interface GetSimulationQueryResult {
  rows: SimulationSession[];
}

export const CHANNEL_LABEL_KEYS: Record<SimulationChannelType, string> = {
  chat: 'simulations.channel_chat',
  email: 'simulations.channel_email',
  voice: 'simulations.channel_voice',
};

export const CADENCE_LABEL_KEYS: Record<ScheduleCadence, string> = {
  weekdays: 'simulations.schedule_frequency_weekdays',
  weekly: 'simulations.schedule_frequency_weekly',
  monthly: 'simulations.schedule_frequency_monthly',
};

// Narrow to Badge's `flavour` union so callsites don't need to re-cast.
export type BadgeFlavour =
  'informational' | 'successful' | 'warning' | 'ai' | 'destructive' | 'neutral';

export const RUN_STATUS_BADGE_FLAVOUR: Record<RunStatus, BadgeFlavour> = {
  pending: 'neutral',
  running: 'informational',
  completed: 'successful',
  failed: 'destructive',
  canceled: 'neutral',
};

export const RUN_STATUS_TRANSLATION_KEYS: Record<RunStatus, string> = {
  pending: 'simulations.pending',
  running: 'simulations.in_progress',
  completed: 'simulations.completed',
  failed: 'simulations.failed',
  canceled: 'simulations.canceled',
};

export const RUN_STATUSES: RunStatus[] = ['pending', 'running', 'completed', 'failed', 'canceled'];

export const RESULT_STATUSES: EvaluationStatus[] = ['pass', 'fail', 'error'];

export const EVALUATION_STATUS_BADGE_FLAVOUR: Record<EvaluationStatus, BadgeFlavour> = {
  pending: 'neutral',
  running: 'informational',
  pass: 'successful',
  fail: 'destructive',
  error: 'destructive',
};

export const EVALUATION_STATUS_TRANSLATION_KEYS: Record<EvaluationStatus, string> = {
  pass: 'simulations.success',
  fail: 'simulations.fail',
  error: 'simulations.error',
  pending: 'simulations.pending',
  running: 'simulations.in_progress',
};
